import type { FunctionComponent } from 'react';
import { useState } from 'react';
import React, { useMemo, useCallback, useEffect } from 'react';
import Box from '@material-ui/core/Box';
import map from 'lodash/map';
import join from 'lodash/join';
import { Button } from '@material-ui/core';
import keys from 'lodash/keys';
import filter from 'lodash/filter';
import some from 'lodash/some';
import Typography from '@material-ui/core/Typography';

import { StepButton } from '../components/step_container/components/step_button/StepButton';
import { StepContainer } from '../components/step_container/StepContainer';
import { guardedFunc } from '../utils/guarded_function';
import type { StepManagerEffectReturnType } from '../steps/types/step_manager';
import type { SelectedServiceType } from '../types/app_data';
import { SERVICES_SPLIT_OPTIONS } from '../types/service_split';
import { useStepManager } from '../steps';
import { trackEvent } from '../utils/analytics';
import { ServiceSplitIcon } from '../components/icon/service_split_icon/ServiceSplitIcon';
import { getSelectedServicesArray } from '../utils/service_split';

const STEP_NAME = 'askSelectService';

const initialServiceOptions: SelectedServiceType = {
  insurance: false,
  garage: false,
  dwep: false,
  refi: false,
};

/**
 * ServiceSplit page
 */
const ServiceSplit: FunctionComponent<StepManagerEffectReturnType> = () => {
  const {
    appData,
    direction,
    stepIdx,
    stepCnt,
    goNext: origGoNext,
    canGoNext,
    goBack: origGoBack,
    canGoBack,
  } = useStepManager(STEP_NAME);
  const goBack = useMemo(() => guardedFunc(origGoBack, canGoBack), [origGoBack, canGoBack]);

  useEffect(() => {
    void trackEvent('CreateAccountServiceSplitPageEnter');
  }, []);

  const [selectedOptions, setSelectedOptions] = useState(appData.selectedService || initialServiceOptions);

  const onClickOption = useCallback((option) => {
    setSelectedOptions((prev) => {
      const next = { ...prev };
      next[option.value] = !next[option.value];
      return next;
    });
  }, []);

  const isValid = useMemo(() => {
    return some(selectedOptions, (value) => !!value);
  }, [selectedOptions]);

  const onClickContinue = useCallback(() => {
    void trackEvent('CreateAccountServiceSelected', {
      eventLabel: join(
        filter(keys(selectedOptions), (key) => selectedOptions[key]),
        ','
      ),
    });

    void origGoNext({
      ...appData,
      serviceSplitSelectedService: selectedOptions,
      selectedService: selectedOptions,
      onlyInsurance:
        selectedOptions.insurance &&
        getSelectedServicesArray(selectedOptions).length === 1 &&
        // onboarding lite is for invitations process.
        !appData.isOnboardingLite &&
        // askICVBefore is for non-partner traffic
        appData.featureData?.askICVBeforeOnboarding,
    });
  }, [appData, origGoNext, selectedOptions]);

  const goNext = useMemo(() => guardedFunc(origGoNext, canGoNext && isValid), [origGoNext, canGoNext, isValid]);

  return (
    <StepContainer
      route={STEP_NAME}
      direction={direction}
      stepIdx={stepIdx}
      stepCnt={stepCnt}
      goNext={goNext}
      goBack={goBack}
      showLoader={false}
      isMobile={appData.isMobileAgent}
      icon={ServiceSplitIcon}
      iconText="Hi there, it's so nice to meet you"
      title="Which services are you interested in?"
      categoryName="SELECT SERVICES"
    >
      <div>
        <div>
          <Box mb={[1.5, 2]}>
            <StepButton
              checkbox
              id={`${SERVICES_SPLIT_OPTIONS[0].trackLabel}`}
              selected={SERVICES_SPLIT_OPTIONS[0].isSelected(selectedOptions)}
              fullWidth
              noLeftPadding
              useImageLayout
              onClick={() => {
                void onClickOption(SERVICES_SPLIT_OPTIONS[0]);
              }}
              startIcon={SERVICES_SPLIT_OPTIONS[0].image}
            >
              <Box>
                <Typography className="option-title">{SERVICES_SPLIT_OPTIONS[0].title}</Typography>
                <Typography className="option-description">MOST POPULAR</Typography>
              </Box>
            </StepButton>
          </Box>
          <div>
            {map(SERVICES_SPLIT_OPTIONS.slice(1), (option, idx) => (
              <Box key={idx} mb={[1.5, 2]}>
                <StepButton
                  checkbox
                  id={`${option.trackLabel}`}
                  selected={option.isSelected(selectedOptions)}
                  fullWidth
                  noLeftPadding
                  useImageLayout
                  onClick={() => {
                    void onClickOption(option);
                  }}
                  startIcon={option.image}
                >
                  <Box>
                    <Typography className="option-title">{option.title}</Typography>
                  </Box>
                </StepButton>
              </Box>
            ))}
          </div>
        </div>
        <Box mt={4}>
          <Button
            color="primary"
            variant="contained"
            disableElevation
            fullWidth
            onClick={onClickContinue}
            disabled={!isValid}
          >
            Continue
          </Button>
        </Box>
      </div>
    </StepContainer>
  );
};

export default ServiceSplit;
